import { useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { type ChartConfig } from '@/components/ui/chart';

import { AbsoluteChangeIndicator } from './AbsoluteChangeIndicator';
import { useVoiceSessions, type VoiceSessionsData } from './hooks/useVoiceSessions';
import { getComparisonPeriodKey } from './PercentChangeIndicator';
import { VOICE_METRIC_CHART_CONFIGS, type VoiceMetricKey } from './voiceSessionsConfig';
import { VoiceSessionsExport } from './VoiceSessionsExport';
import { useComparisonMode } from '../../hooks/useComparisonMode';
import { selectAnalyticsDates } from '../../redux/selectors';
import {
  formatCountDelta,
  formatDurationDelta,
  formatPercentDelta,
} from '../../utils/deltaFormatters';
import { formatDateRangeLabel, isSingleDayRange } from '../../utils/formatDateRangeLabel';
import { getComparisonDates } from '../../utils/getComparisonDates';
import { KpiTile } from '../KpiTile/KpiTile';
import { ChartError, TimeseriesChart } from '../TimeseriesChart/TimeseriesChart';

const CHART_COLOR = 'var(--chart-1)';

const EMPTY_VALUE_PLACEHOLDER = '-';

const DELTA_FORMATTERS: Record<VoiceMetricKey, (delta: number) => string> = {
  totalSessions: formatCountDelta,
  totalMinutes: formatDurationDelta,
  avgSessionLength: formatDurationDelta,
  voicemailRate: formatPercentDelta,
};

interface KpiTileDefinition {
  key: VoiceMetricKey;
  labelKey: string;
  formattedValue: string;
  current: number;
  previous: number;
  isMetricEmpty: boolean;
  tooltipKey?: string;
}

const buildKpiTiles = (data: VoiceSessionsData, isEmpty: boolean): KpiTileDefinition[] => {
  const { kpiValues, durationCount, totalDurationSeconds } = data;
  // Avg session length is undefined when no session had a computable duration,
  // even if the period has voice sessions. Render the placeholder rather than a
  // misleading "0s".
  const isAvgEmpty = isEmpty || durationCount.current === 0;

  const format = (key: VoiceMetricKey, empty: boolean): string =>
    empty
      ? EMPTY_VALUE_PLACEHOLDER
      : VOICE_METRIC_CHART_CONFIGS[key].valueFormatter(kpiValues[key].current);

  return [
    {
      key: 'totalSessions',
      labelKey: VOICE_METRIC_CHART_CONFIGS.totalSessions.label,
      formattedValue: format('totalSessions', isEmpty),
      current: kpiValues.totalSessions.current,
      previous: kpiValues.totalSessions.previous,
      isMetricEmpty: isEmpty,
      tooltipKey: 'analytics.voice_total_sessions_tooltip',
    },
    {
      key: 'totalMinutes',
      labelKey: VOICE_METRIC_CHART_CONFIGS.totalMinutes.label,
      formattedValue: format('totalMinutes', isEmpty),
      // Seconds, so the delta rounds to the tile's resolution, not whole minutes.
      current: totalDurationSeconds.current,
      previous: totalDurationSeconds.previous,
      isMetricEmpty: isEmpty,
      tooltipKey: 'analytics.voice_total_minutes_tooltip',
    },
    {
      key: 'avgSessionLength',
      labelKey: VOICE_METRIC_CHART_CONFIGS.avgSessionLength.label,
      formattedValue: format('avgSessionLength', isAvgEmpty),
      current: kpiValues.avgSessionLength.current,
      previous: kpiValues.avgSessionLength.previous,
      isMetricEmpty: isAvgEmpty,
      tooltipKey: 'analytics.voice_avg_session_length_tooltip',
    },
    {
      key: 'voicemailRate',
      labelKey: VOICE_METRIC_CHART_CONFIGS.voicemailRate.label,
      formattedValue: format('voicemailRate', isEmpty),
      current: kpiValues.voicemailRate.current,
      previous: kpiValues.voicemailRate.previous,
      isMetricEmpty: isEmpty,
    },
  ];
};

/**
 * Voice Sessions widget.
 *
 * Four KPI tiles (calls, call time, avg answered conversation, voicemail rate)
 * backed by `voice_overview`, with a time-series chart (per-bucket data from
 * `voice_timeseries`) that follows the selected tile. Rendered only for accounts
 * with a voice capability (see the gate in the AI Agents page).
 */
export const VoiceSessions = () => {
  const { t } = useTranslation();
  const { isComparing } = useComparisonMode();
  const { startDate, endDate } = useSelector(selectAnalyticsDates);
  const { data, isLoading, error, isEmpty, refetch } = useVoiceSessions();
  const [selectedMetric, setSelectedMetric] = useState<VoiceMetricKey>('totalSessions');

  const comparisonLabel = t(getComparisonPeriodKey(startDate, endDate));
  const isSingleDay = isSingleDayRange(startDate, endDate);
  const dateRange = formatDateRangeLabel(startDate, endDate);
  const currentConfig = VOICE_METRIC_CHART_CONFIGS[selectedMetric];

  // Drive the chart x-axis from the selected range (and the previous-period
  // range when comparing) so every day renders even without returned data.
  const period = useMemo(() => ({ start: startDate, end: endDate }), [startDate, endDate]);
  const comparisonPeriod = useMemo(() => {
    const { startDate: start, endDate: end } = getComparisonDates(startDate, endDate);
    return { start, end };
  }, [startDate, endDate]);

  const chartConfig = useMemo<ChartConfig>(
    () => ({ value: { label: t(currentConfig.label), color: CHART_COLOR } }),
    [currentConfig, t]
  );

  const kpiTiles = useMemo(() => buildKpiTiles(data, isEmpty), [data, isEmpty]);

  const timeseries = useMemo(
    () => ({
      isLoading,
      error: error ?? undefined,
      data: data.timeseries[selectedMetric],
      onRefetch: () => undefined,
    }),
    [error, data.timeseries, isLoading, selectedMetric]
  );

  const comparisonTimeseries = useMemo(
    () => ({
      isLoading,
      error: error ?? undefined,
      data: data.comparisonTimeseries[selectedMetric],
    }),
    [error, data.comparisonTimeseries, isLoading, selectedMetric]
  );

  return (
    <Card className="rounded-lg bg-background text-foreground shadow-none">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex flex-col gap-2">
            <CardTitle>{t('analytics.voice_sessions')}</CardTitle>
            <CardDescription>{dateRange}</CardDescription>
          </div>
          {!isEmpty && !error && <VoiceSessionsExport />}
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {error ? (
          <ChartError onRetry={refetch} t={t} />
        ) : (
          <>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {kpiTiles.map((tile) => (
                <KpiTile
                  key={tile.key}
                  label={t(tile.labelKey)}
                  value={tile.formattedValue}
                  isLoading={isLoading}
                  tooltip={tile.tooltipKey ? t(tile.tooltipKey) : undefined}
                  isSelected={selectedMetric === tile.key}
                  onClick={() => setSelectedMetric(tile.key)}
                  comparison={
                    isComparing &&
                    !tile.isMetricEmpty && (
                      <AbsoluteChangeIndicator
                        current={tile.current}
                        previous={tile.previous}
                        formatDelta={DELTA_FORMATTERS[tile.key]}
                        precision={0}
                        comparisonLabel={comparisonLabel}
                        className="mt-1"
                      />
                    )
                  }
                />
              ))}
            </div>

            {/* Single-day rule: charts only make sense for multi-day ranges. */}
            {!isSingleDay && timeseries.data.length > 0 && (
              <TimeseriesChart
                timeseries={timeseries}
                chartConfig={chartConfig}
                currentConfig={currentConfig}
                t={t}
                period={period}
                comparisonPeriod={comparisonPeriod}
                isComparing={isComparing}
                comparisonTimeseries={comparisonTimeseries}
              />
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};
